import type { TopFilter } from './caseTaxonomyFilter';
import {
  ALL_CASE_TAXONOMIES,
  getDefaultStages,
  getMatterTypeForCase,
  getTaxonomyConfigForTopFilter,
  inferCaseCategoryForCase,
  resolveStartingStageFromTaxonomy,
  type CaseTaxonomyContextInput,
} from './caseTaxonomyRegistry';
import { normalizeStageLabel, stageLabelsMatch } from './stageAliases';

export type CaseStageContextInput = CaseTaxonomyContextInput & {
  stage?: string;
  status?: string;
  isCustomCase?: boolean;
  closestStandardArea?: string;
};

export { stageLabelsMatch };

const HIDDEN_STAGE_LABELS = new Set(['case created']);

function addUniqueStageLabel(list: string[], label: string): void {
  const trimmed = label.trim();
  if (!trimmed || HIDDEN_STAGE_LABELS.has(trimmed.toLowerCase())) return;
  if (list.some((existing) => stageLabelsMatch(existing, trimmed))) return;
  list.push(trimmed);
}

function collectStagesFromTaxonomy(types: { defaultStages?: string[] }[]): string[] {
  const out: string[] = [];
  for (const type of types) {
    for (const stage of type.defaultStages ?? []) {
      addUniqueStageLabel(out, stage);
    }
  }
  return out;
}

/** Canonical workflow stage labels for a matter category (same source as Case stages column). */
export function getTaxonomyStagesForTopFilter(topFilter: TopFilter): string[] {
  if (topFilter === 'All') {
    const out: string[] = [];
    for (const config of ALL_CASE_TAXONOMIES) {
      for (const stage of collectStagesFromTaxonomy(config.types)) {
        addUniqueStageLabel(out, stage);
      }
    }
    return out;
  }
  if (topFilter === 'Other') {
    const out: string[] = [];
    for (const entry of ALL_CASE_TAXONOMIES) {
      const otherType = entry.types.find((t) => t.id.startsWith('Other '));
      if (otherType?.defaultStages) {
        for (const stage of otherType.defaultStages) {
          addUniqueStageLabel(out, stage);
        }
      }
    }
    return out;
  }
  const config = getTaxonomyConfigForTopFilter(topFilter);
  return config ? collectStagesFromTaxonomy(config.types) : [];
}

/** Filter dropdown options: taxonomy stages for the active category, plus any in-use labels. */
export function buildStageFilterOptions(
  cases: CaseStageContextInput[],
  topFilter: TopFilter = 'All',
): string[] {
  const out = getTaxonomyStagesForTopFilter(topFilter);
  for (const c of cases) {
    addUniqueStageLabel(out, resolveCurrentCaseStage(c));
  }
  return out;
}

export function caseMatchesStageFilter(
  caseData: CaseStageContextInput,
  filterStage: string,
): boolean {
  if (!filterStage.trim()) return true;
  const current = resolveCurrentCaseStage(caseData);
  return stageLabelsMatch(current, filterStage);
}

/** First stage when creating a case (aligned with Case stages column). */
export function resolveStartingCaseStage(
  caseData: CaseStageContextInput,
  representingSide?: string,
): string {
  const enriched: CaseStageContextInput = {
    ...caseData,
    caseCategory: inferCaseCategoryForCase(caseData),
  };
  return resolveStartingStageFromTaxonomy(enriched, representingSide);
}

export function getCaseStageOptions(caseData: CaseStageContextInput): string[] {
  const enriched: CaseStageContextInput = {
    ...caseData,
    caseCategory: inferCaseCategoryForCase(caseData),
  };
  return getDefaultStages(enriched);
}

/** Stage list for UI; normalizes stored legacy labels into the canonical list. */
export function resolveCaseStagesList(caseData: CaseStageContextInput): string[] {
  const enriched: CaseStageContextInput = {
    ...caseData,
    caseCategory: inferCaseCategoryForCase(caseData),
  };
  const base = getCaseStageOptions(enriched);
  const stored = caseData.stage?.trim();
  if (!stored) {
    return base.length > 0 ? base : [resolveStartingCaseStage(enriched)];
  }

  const current = normalizeStageLabel(stored, enriched);
  if (base.some((s) => stageLabelsMatch(s, current))) {
    return base.length > 0 ? base : [current];
  }

  return base.length > 0 ? [...base, current] : [current];
}

export function resolveCurrentCaseStage(caseData: CaseStageContextInput): string {
  const enriched: CaseStageContextInput = {
    ...caseData,
    caseCategory: inferCaseCategoryForCase(caseData),
  };
  const stages = getCaseStageOptions(enriched);
  const stored = caseData.stage?.trim();
  if (stored) {
    const normalized = normalizeStageLabel(stored, enriched);
    const inList = stages.find((s) => stageLabelsMatch(s, normalized));
    return inList ?? normalized;
  }
  return stages[0] ?? resolveStartingCaseStage(enriched) ?? 'Case created';
}

/** Match a workflow stage label from the case type's canonical stage list. */
export function findCaseStageLabel(
  caseData: CaseStageContextInput,
  matcher: RegExp | string,
  fallback?: string,
): string {
  const stages = getCaseStageOptions({
    ...caseData,
    caseCategory: inferCaseCategoryForCase(caseData),
  });
  if (typeof matcher === 'string') {
    const direct = stages.find((s) => stageLabelsMatch(s, matcher));
    if (direct) return direct;
    return fallback ?? matcher;
  }
  const hit = stages.find((s) => matcher.test(s));
  return hit ?? fallback ?? stages[0] ?? 'Other';
}

/** First preparation-style stage for the matter (Plaint, petition drafting, etc.). */
export function resolvePreparationStageLabel(caseData: CaseStageContextInput): string {
  const enriched = {
    ...caseData,
    caseCategory: inferCaseCategoryForCase(caseData),
  };
  const matter = getMatterTypeForCase(enriched);
  if (matter?.defaultStages?.[0]) {
    const prep = matter.defaultStages[0];
    if (/case preparation|pre-filing|pre filing|record review/i.test(prep)) {
      return prep;
    }
  }
  return findCaseStageLabel(
    enriched,
    /case preparation|pre-filing|pre filing|record review|petition drafting|bail petition|suit \/ plaint/i,
    'Case Preparation',
  );
}
